import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useAppContextNonNull } from "@contexts/AppContext";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import { OwFnTable } from "@components/shared/OwFnTable/OwFnTable";
import PaginationBar from "@components/shared/PaginationBar/PaginationBar";
import OwBadge from "@components/shared/OwBadge/OwBadge";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { OwDeleteEditActions } from "@components/shared/OwDeleteEditActions/OwDeleteEditActions";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import VendorService from "@services/VendorService";
import IConnection_Token from "@models/IConnection_Token";
import IOwTableCols from "@models/IOwTableCols";
import IGetVendorsQuery from "@models/DTO/IGetVendorsQuery";
import { UnanetProjectMappingEdit } from "./UnanetProjectMappingEdit";
import "./UnanetProjectMapping.scss";
import { SearchSection } from "@components/shared/SearchSection/SearchSection";
import Breadcrumbs from "@components/shared/Breadcrumbs/Breadcrumbs";
import { useLocation } from "react-router-dom";

type FilterMode = "all" | "mapped" | "unmapped";

export const UnanetProjectMapping = () => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();
    const location = useLocation();
    const basePath = location.pathname.replace(/\/unanet\/project-mapping$/, "");

    const [vendors, setVendors] = useState<IConnection_Token[]>([]);
    const [totalVendors, setTotalVendors] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [limit, setLimit] = useState(25);
    const [offset, setOffset] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterMode, setFilterMode] = useState<FilterMode>("all");

    const fetchVendors = useCallback(async () => {
        setIsLoading(true);
        try {
            const query: IGetVendorsQuery = {
                limit: String(limit),
                offset: String(offset),
                ...(searchQuery ? { name: searchQuery } : {}),
            };
            const data = await VendorService.getVendors(query, userModel, supplierModel);
            setVendors(data.supplierVendorTokens || []);
            setTotalVendors(data.totalVendors || 0);
        } catch (e) {
            console.error("Error fetching vendors:", e);
        } finally {
            setIsLoading(false);
            setIsInitialLoad(false);
        }
    }, [limit, offset, searchQuery, userModel, supplierModel]);

    useEffect(() => {
        fetchVendors();
    }, [fetchVendors]);

    const filteredVendors = useMemo(() => {
        if (filterMode === "all") return vendors;
        return vendors.filter((token) => {
            const isMapped = token.Vendor?.Vendor_Integration?.unanet_project_key != null;
            return filterMode === "mapped" ? isMapped : !isMapped;
        });
    }, [vendors, filterMode]);

    const mappedCount = useMemo(() => {
        return vendors.filter((t) => t.Vendor?.Vendor_Integration?.unanet_project_key != null).length;
    }, [vendors]);

    const openEditSidebar = useCallback(
        (vendorToken: IConnection_Token) => {
            uiContext.setSidebarComponent(
                <UnanetProjectMappingEdit
                    vendorToken={vendorToken}
                    onClose={(refresh) => {
                        uiContext.toggleSidebar(false);
                        if (refresh) {
                            fetchVendors();
                        }
                    }}
                />
            );
            uiContext.toggleSidebar(true);
        },
        [uiContext, fetchVendors]
    );

    const onPaginationChanged = (newOffset: number, newLimit: number) => {
        setOffset(newOffset);
        setLimit(newLimit);
    };

    const onSearchChange = (value: string) => {
        setSearchQuery(value);
        setOffset(0);
    };

    const columns: IOwTableCols[] = useMemo(
        () =>
            [
                {
                    title: "vendor_name",
                    valueProp: "name",
                    baseObject: "Vendor",
                    className: "",
                    isVendorOverride: true,
                },
                {
                    title: "unanet_project",
                    valueProp: "",
                    renderFnEmitsRow: true,
                    renderFn: (row: IConnection_Token) => {
                        const vi = row.Vendor?.Vendor_Integration;
                        if (vi?.unanet_project_key != null) {
                            return (
                                <span>
                                    {vi.unanet_project_code} - {vi.unanet_project_title ?? ""}
                                </span>
                            );
                        }
                        return (
                            <span className="text-muted">
                                <OwLabel title={"unmapped"} noBottomMargin />
                            </span>
                        );
                    },
                },
                {
                    title: "status",
                    valueProp: "",
                    renderFnEmitsRow: true,
                    className: "text-center",
                    renderFn: (row: IConnection_Token) => {
                        const isMapped = row.Vendor?.Vendor_Integration?.unanet_project_key != null;
                        return <OwBadge title={isMapped ? "mapped" : "unmapped"} bgColor={isMapped ? "bg-success" : "bg-secondary"} />;
                    },
                },
                {
                    title: "unanet_req_tasks",
                    valueProp: "",
                    renderFnEmitsRow: true,
                    className: "text-center",
                    renderFn: (row: IConnection_Token) => {
                        if (row.Vendor?.Vendor_Integration?.unanet_requires_tasks) {
                            return <i className="fas fa-check text-success" />;
                        }
                        return null;
                    },
                },
                {
                    title: "",
                    valueProp: "",
                    isActionsCol: true,
                    className: "text-end",
                    renderFnEmitsRow: true,
                    renderFn: (row: IConnection_Token) => {
                        return <OwDeleteEditActions isEditMode={false} hideTrash={true} onEditClickFn={() => openEditSidebar(row)} />;
                    },
                },
            ] as IOwTableCols[],
        [openEditSidebar]
    );

    return (
        <div className={"UnanetProjectMapping"}>
            <Breadcrumbs
                crumbs={[
                    { title: "vendors", url: basePath },
                    { title: "unanet_project_mapping", url: `${basePath}/unanet/project-mapping` },
                ]}
            />

            <OwTitle title={"unanet_project_mapping"} leftIconName="fas fa-project-diagram" />

            <div className={"UnanetProjectMapping__summary mb-3"}>
                <span className={"text-muted"}>
                    {mappedCount} of {vendors.length} vendors mapped (this page)
                </span>
            </div>

            <div className={"UnanetProjectMapping__controls mb-3"}>
                <SearchSection>
                    <div className={"row align-items-center"}>
                        <div className={"col-auto"}>
                            <OwFnInput
                                name={"search_vendors"}
                                placeholder={"search_vendors"}
                                value={searchQuery}
                                fn={(e: ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
                                enableDebounce={true}
                                debounceMs={400}
                                className={"form-control"}
                            />
                        </div>
                        <div className={"col-auto"}>
                            <div className={"UnanetProjectMapping__filters"}>
                                <OwButton
                                    title={"show_all"}
                                    className={`btn btn-sm ${filterMode === "all" ? "btn-primary" : "btn-outline-secondary"}`}
                                    fn={() => setFilterMode("all")}
                                />
                                <OwButton
                                    title={"mapped_only"}
                                    className={`btn btn-sm ${filterMode === "mapped" ? "btn-success" : "btn-outline-secondary"}`}
                                    fn={() => setFilterMode("mapped")}
                                />
                                <OwButton
                                    title={"unmapped_only"}
                                    className={`btn btn-sm ${filterMode === "unmapped" ? "btn-secondary" : "btn-outline-secondary"}`}
                                    fn={() => setFilterMode("unmapped")}
                                />
                            </div>
                        </div>
                    </div>
                </SearchSection>
            </div>

            <OwFnTable
                columns={columns}
                dataSet={filteredVendors}
                dataFetch={isLoading}
                initialData={isInitialLoad}
                tableConfig={{
                    tableHover: true,
                    tableStriped: true,
                }}
            />

            {!isInitialLoad && (
                <PaginationBar limit={limit} offset={offset} totalRecords={totalVendors} onPaginationChanged={onPaginationChanged} />
            )}
        </div>
    );
};
